/** @format */

import {
	CardNumberElement,
	CardExpiryElement,
	CardCvcElement,
	useElements,
	useStripe,
} from "@stripe/react-stripe-js";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUser } from "../../../redux/selectors/userSelector";
import {
	selectPaymentMethodLoading,
	selectPaymentMethodError,
	selectPaymentMethodSuccess,
	selectDefaultPaymentMethod,
} from "../../../redux/selectors/paymentMethodSelector";
import { useEffect, useState } from "react";
import { AppDispatch } from "../../../redux/store/store";
import {
	registerPaymentMethod,
	getDefaultPaymentMethod,
	updatePaymentMethod,
} from "../../../redux/actions/paymentMethodAction";
import {
	clearPaymentMethodError,
	clearPaymentMethodSuccess,
} from "../../../redux/slices/paymentMethodSlice";
import React from "react";
import {
	Avatar,
	Box,
	Button,
	Container,
	Dialog,
	DialogContent,
	Grid,
	IconButton,
	MenuItem,
	Paper,
	TextField,
	Typography,
	Alert,
	Chip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import {
	LoadingSpinner,
	NotificationModal,
} from "../../allComponents/AllComponents";
import { buttonColors, colors } from "../../../utility/types/constant";
import { StyledTypography } from "../../circle-leader-application/styles";
import CloseIcon from "@mui/icons-material/Close";

// Country options with 2-character ISO codes (Stripe requirement)
const COUNTRY_OPTIONS = [
	{ code: "US", name: "United States" },
	{ code: "CA", name: "Canada" },
	{ code: "GB", name: "United Kingdom" },
	{ code: "AU", name: "Australia" },
	{ code: "DE", name: "Germany" },
	{ code: "FR", name: "France" },
	{ code: "ES", name: "Spain" },
	{ code: "IT", name: "Italy" },
	{ code: "NL", name: "Netherlands" },
	{ code: "BE", name: "Belgium" },
	{ code: "AT", name: "Austria" },
	{ code: "CH", name: "Switzerland" },
	{ code: "IE", name: "Ireland" },
	{ code: "NZ", name: "New Zealand" },
	{ code: "SE", name: "Sweden" },
	{ code: "NO", name: "Norway" },
	{ code: "DK", name: "Denmark" },
	{ code: "FI", name: "Finland" },
	{ code: "PT", name: "Portugal" },
	{ code: "PL", name: "Poland" },
	{ code: "MX", name: "Mexico" },
	{ code: "BR", name: "Brazil" },
	{ code: "JP", name: "Japan" },
	{ code: "SG", name: "Singapore" },
	{ code: "HK", name: "Hong Kong" },
];

interface UpdatePaymentMethodModalProps {
	open: boolean;
	handleClose: () => void;
}

interface ICardRegistrationForm {
	firstName: string;
	lastName: string;
	streetName: string;
	city: string;
	state: string;
	zipCode: string;
	country: string;
}

// Validation schema for card registration
const cardRegistrationSchema = Yup.object().shape({
	firstName: Yup.string().required("First name is required"),
	lastName: Yup.string().required("Last name is required"),
	streetName: Yup.string().required("Street name is required"),
	city: Yup.string().required("City is required"),
	state: Yup.string().required("State is required"),
	zipCode: Yup.string().required("Zip code is required"),
	country: Yup.string()
		.required("Country is required")
		.length(2, "Please select a country from the list"),
});

const UpdatePaymentMethodModal = (props: UpdatePaymentMethodModalProps) => {
	const dispatch = useDispatch<AppDispatch>();
	const stripe = useStripe();
	const { open, handleClose } = props;
	const elements = useElements();

	const userData = useSelector(selectCurrentUser);
	const isLoading = useSelector(selectPaymentMethodLoading);
	const error = useSelector(selectPaymentMethodError);
	const successMessage = useSelector(selectPaymentMethodSuccess);
	const existingCard = useSelector(selectDefaultPaymentMethod);

	const [openNotificationModal, setOpenNotificationModal] = useState(false);
	const [cardError, setCardError] = useState<string>("");
	const [cardNumberComplete, setCardNumberComplete] = useState(false);
	const [cardExpiryComplete, setCardExpiryComplete] = useState(false);
	const [cardCvcComplete, setCardCvcComplete] = useState(false);

	const isCardComplete =
		cardNumberComplete && cardExpiryComplete && cardCvcComplete;

	// Helper function to convert country name to code
	const getCountryCode = (country: string): string => {
		if (!country) return "US"; // Default to US
		// If it's already a 2-character code, return it
		if (country.length === 2) return country.toUpperCase();
		// Try to find matching country
		const found = COUNTRY_OPTIONS.find(
			(c) => c.name.toLowerCase() === country.toLowerCase(),
		);
		return found?.code ?? "US";
	};

	const initialValues: ICardRegistrationForm = {
		firstName: userData?.firstName ?? "",
		lastName: userData?.lastName ?? "",
		streetName: userData?.address?.streetName ?? "",
		city: userData?.address?.city ?? "",
		state: userData?.address?.state ?? "",
		zipCode: userData?.address?.zipCode ?? "",
		country: getCountryCode(userData?.address?.country ?? ""),
	};

	// Fetch default payment method on mount
	useEffect(() => {
		if (open) {
			dispatch(getDefaultPaymentMethod());
		}
	}, [dispatch, open]);

	// Clear errors on mount
	useEffect(() => {
		dispatch(clearPaymentMethodError());
		dispatch(clearPaymentMethodSuccess());
		setCardError("");
	}, [dispatch]);

	// Show success modal when card is registered
	useEffect(() => {
		if (successMessage) {
			setOpenNotificationModal(true);
			setTimeout(() => {
				setOpenNotificationModal(false);
				dispatch(clearPaymentMethodSuccess());
				handleClose();
				// Optionally navigate to user profile or dashboard
				// navigate(`/user/${userData?._id}`);
			}, 3000);
		}
	}, [successMessage, dispatch, handleClose]);

	const handleRegisterCard = async (values: ICardRegistrationForm) => {
		if (!stripe || !elements) {
			setCardError("Stripe.js hasn't loaded yet. Please try again.");
			return;
		}

		const cardNumberElement = elements.getElement(CardNumberElement);

		if (!cardNumberElement) {
			setCardError("Please provide your card details.");
			return;
		}

		setCardError("");

		try {
			// Create payment method with Stripe
			const { error: stripeError, paymentMethod } =
				await stripe.createPaymentMethod({
					type: "card",
					card: cardNumberElement,
					billing_details: {
						name: `${values.firstName} ${values.lastName}`,
						address: {
							line1: values.streetName,
							city: values.city,
							state: values.state,
							postal_code: values.zipCode,
							country: values.country,
						},
					},
				});

			if (stripeError) {
				setCardError(stripeError.message || "Card validation failed");
				return;
			}

			if (!paymentMethod) {
				setCardError("Failed to create payment method");
				return;
			}

			// Build payload for backend
			const payload = {
				paymentMethodId: paymentMethod.id,
				billingName: `${values.firstName} ${values.lastName}`,
				postalCode: values.zipCode,
			};

			// If user already has a card, update; otherwise register
			if (existingCard) {
				await dispatch(updatePaymentMethod(payload)).unwrap();
				// Refresh the default payment method to get updated last4 instantly
				await dispatch(getDefaultPaymentMethod());
			} else {
				await dispatch(registerPaymentMethod(payload)).unwrap();
			}

			// Clear the card elements after successful registration/update
			cardNumberElement.clear();
			elements.getElement(CardExpiryElement)?.clear();
			elements.getElement(CardCvcElement)?.clear();
			setCardNumberComplete(false);
			setCardExpiryComplete(false);
			setCardCvcComplete(false);
		} catch (error: any) {
			console.error("Failed to save card:", error);
			setCardError(error || "Failed to save card. Please try again.");
		}
	};

	const handleCloseModal = () => {
		dispatch(clearPaymentMethodError());
		dispatch(clearPaymentMethodSuccess());
		setCardError("");
		handleClose();
	};

	return (
		<React.Fragment>
			<Dialog
				open={open}
				onClose={handleCloseModal}
				fullWidth
				maxWidth="lg"
				PaperProps={{
					sx: {
						m: { xs: 1, sm: 2 },
						width: { xs: "calc(100% - 16px)", sm: "95%", md: "90%" },
						maxWidth: { xs: "100%", sm: "1100px", md: "1200px" },
					},
				}}>
				<DialogContent sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
					<IconButton
						aria-label="close"
						onClick={handleCloseModal}
						sx={{
							position: "absolute",
							right: 8,
							top: 8,
							color: colors.white,
							backgroundColor: colors.cancel,
							"&:hover": {
								color: colors.white,
								backgroundColor: colors.cancel,
							},
						}}>
						<CloseIcon />
					</IconButton>
					<Container
						component="main"
						maxWidth="lg"
						sx={{ mb: 4, px: { xs: 0, sm: 2, md: 4 } }}>
						<Paper
							elevation={4}
							sx={{
								my: { xs: 1, sm: 2, md: 4 },
								p: { xs: 2, sm: 3, md: 4 },
								width: "100%",
								borderRadius: { xs: 2, md: 3 },
								background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
							}}>
							<Typography
								component="h1"
								variant="h4"
								align="center"
								sx={{
									mb: 1,
									fontWeight: 600,
									color: buttonColors.infoDarkBlue,
								}}>
								{existingCard ? "Update Your Card" : "Add Your Card"}
							</Typography>
							<Typography
								variant="body2"
								align="center"
								sx={{ mb: 2, color: "text.secondary" }}>
								Securely add your payment method for automatic contributions
							</Typography>

							{/* Show existing card if member has one */}
							{existingCard && (
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										mb: 3,
									}}>
									<Chip
										icon={<CreditCardIcon />}
										label={`Current card: ${existingCard.brand.toUpperCase()} •••• ${
											existingCard.last4
										}`}
										color="primary"
										variant="outlined"
										sx={{
											fontSize: "0.95rem",
											py: 2.5,
											px: 1,
											"& .MuiChip-icon": {
												color: buttonColors.infoDarkBlue,
											},
										}}
									/>
								</Box>
							)}

							<Formik
								initialValues={initialValues}
								onSubmit={handleRegisterCard}
								validationSchema={cardRegistrationSchema}
								enableReinitialize>
								{({ errors, touched }) => (
									<Form>
										<Grid container spacing={3}>
											<Grid item xs={12} md={6}>
												<Field name="firstName">
													{({ field, form }: any) => (
														<TextField
															{...field}
															required
															id="firstName"
															label="First Name"
															fullWidth
															variant="outlined"
															margin="normal"
															error={
																form.touched.firstName &&
																Boolean(form.errors.firstName)
															}
															helperText={
																form.touched.firstName && form.errors.firstName
															}
														/>
													)}
												</Field>
											</Grid>
											<Grid item xs={12} md={6}>
												<Field name="lastName">
													{({ field, form }: any) => (
														<TextField
															{...field}
															required
															id="lastName"
															label="Last Name"
															fullWidth
															variant="outlined"
															margin="normal"
															error={
																form.touched.lastName &&
																Boolean(form.errors.lastName)
															}
															helperText={
																form.touched.lastName && form.errors.lastName
															}
														/>
													)}
												</Field>
											</Grid>
											<Grid item xs={12} md={6}>
												<Field name="streetName">
													{({ field, form }: any) => (
														<TextField
															{...field}
															id="streetName"
															label="Street Name"
															variant="outlined"
															fullWidth
															required
															margin="normal"
															error={
																form.touched.streetName &&
																Boolean(form.errors.streetName)
															}
															helperText={
																form.touched.streetName &&
																form.errors.streetName
															}
														/>
													)}
												</Field>
											</Grid>
											<Grid item xs={12} md={6}>
												<Field name="city">
													{({ field, form }: any) => (
														<TextField
															{...field}
															id="city"
															label="City"
															variant="outlined"
															fullWidth
															required
															margin="normal"
															error={
																form.touched.city && Boolean(form.errors.city)
															}
															helperText={form.touched.city && form.errors.city}
														/>
													)}
												</Field>
											</Grid>
											<Grid item xs={12} md={6}>
												<Field name="state">
													{({ field, form }: any) => (
														<TextField
															{...field}
															id="state"
															label="State/Province/Region"
															variant="outlined"
															fullWidth
															required
															margin="normal"
															error={
																form.touched.state && Boolean(form.errors.state)
															}
															helperText={
																form.touched.state && form.errors.state
															}
														/>
													)}
												</Field>
											</Grid>
											<Grid item xs={12} md={6}>
												<Field name="zipCode">
													{({ field, form }: any) => (
														<TextField
															{...field}
															id="zipCode"
															label="Zip Code"
															variant="outlined"
															fullWidth
															required
															margin="normal"
															error={
																form.touched.zipCode &&
																Boolean(form.errors.zipCode)
															}
															helperText={
																form.touched.zipCode && form.errors.zipCode
															}
														/>
													)}
												</Field>
											</Grid>
											<Grid item xs={12} md={6}>
												<Field name="country">
													{({ field, form }: any) => (
														<TextField
															{...field}
															id="country"
															label="Country"
															variant="outlined"
															fullWidth
															required
															select
															margin="normal"
															error={
																form.touched.country &&
																Boolean(form.errors.country)
															}
															helperText={
																form.touched.country ?
																	form.errors.country
																:	"Select your country (2-letter code required)"
															}>
															{COUNTRY_OPTIONS.map((option) => (
																<MenuItem key={option.code} value={option.code}>
																	{option.name} ({option.code})
																</MenuItem>
															))}
														</TextField>
													)}
												</Field>
											</Grid>

											<Grid item xs={12} sx={{ mt: 2 }}>
												<Typography
													variant="subtitle2"
													sx={{ mb: 1, fontWeight: 500 }}>
													Card Information *
												</Typography>

												{/* Card Number - Full Width */}
												<Box
													sx={{
														border: 1.5,
														borderColor: "rgba(0, 0, 0, 0.23)",
														borderRadius: "4px",
														padding: { xs: "14px 12px", sm: "16.5px 14px" },
														mb: 2,
														"&:hover": {
															borderColor: "rgba(0, 0, 0, 0.87)",
														},
														"&:focus-within": {
															borderColor: buttonColors.infoDarkBlue,
															borderWidth: 2,
														},
													}}>
													<CardNumberElement
														onChange={(e) => setCardNumberComplete(e.complete)}
														options={{
															style: {
																base: {
																	fontSize: "16px",
																	color: "#424770",
																	"::placeholder": { color: "#aab7c4" },
																	fontFamily:
																		'"Roboto", "Helvetica", "Arial", sans-serif',
																	lineHeight: "1.5",
																},
																invalid: { color: "#9e2146" },
															},
															placeholder: "Card number",
														}}
													/>
												</Box>

												{/* Expiry and CVC - Side by Side */}
												<Grid container spacing={2}>
													<Grid item xs={6}>
														<Box
															sx={{
																border: 1.5,
																borderColor: "rgba(0, 0, 0, 0.23)",
																borderRadius: "4px",
																padding: { xs: "14px 12px", sm: "16.5px 14px" },
																"&:hover": {
																	borderColor: "rgba(0, 0, 0, 0.87)",
																},
																"&:focus-within": {
																	borderColor: buttonColors.infoDarkBlue,
																	borderWidth: 2,
																},
															}}>
															<CardExpiryElement
																onChange={(e) =>
																	setCardExpiryComplete(e.complete)
																}
																options={{
																	style: {
																		base: {
																			fontSize: "16px",
																			color: "#424770",
																			"::placeholder": { color: "#aab7c4" },
																			fontFamily:
																				'"Roboto", "Helvetica", "Arial", sans-serif',
																			lineHeight: "1.5",
																		},
																		invalid: { color: "#9e2146" },
																	},
																	placeholder: "MM / YY",
																}}
															/>
														</Box>
													</Grid>
													<Grid item xs={6}>
														<Box
															sx={{
																border: 1.5,
																borderColor: "rgba(0, 0, 0, 0.23)",
																borderRadius: "4px",
																padding: { xs: "14px 12px", sm: "16.5px 14px" },
																"&:hover": {
																	borderColor: "rgba(0, 0, 0, 0.87)",
																},
																"&:focus-within": {
																	borderColor: buttonColors.infoDarkBlue,
																	borderWidth: 2,
																},
															}}>
															<CardCvcElement
																onChange={(e) => setCardCvcComplete(e.complete)}
																options={{
																	style: {
																		base: {
																			fontSize: "16px",
																			color: "#424770",
																			"::placeholder": { color: "#aab7c4" },
																			fontFamily:
																				'"Roboto", "Helvetica", "Arial", sans-serif',
																			lineHeight: "1.5",
																		},
																		invalid: { color: "#9e2146" },
																	},
																	placeholder: "CVC",
																}}
															/>
														</Box>
													</Grid>
												</Grid>
											</Grid>

											{/* Consent Notice */}
											<Grid item xs={12}>
												<Alert
													severity="info"
													icon={<InfoOutlinedIcon />}
													sx={{
														mt: 2,
														borderRadius: 2,
														backgroundColor: "#e3f2fd",
														border: "1px solid #90caf9",
													}}>
													<Typography
														variant="body2"
														sx={{ fontWeight: 500, mb: 0.5 }}>
														🔒 Auto-Payment Consent
													</Typography>
													<Typography
														variant="body2"
														sx={{ color: "text.secondary" }}>
														By adding this card, you consent to automatic
														payments on contribution days for all circles you're
														a member of.
														<strong>
															{" "}
															Never miss a contribution and build trust with
															your circle members effortlessly.
														</strong>
													</Typography>
												</Alert>
											</Grid>

											{/* Display errors */}
											{(error || cardError) && (
												<Grid item xs={12}>
													<Typography
														variant="body2"
														sx={{
															color: "error.main",
															textAlign: "center",
															mt: 1,
														}}>
														{cardError || error}
													</Typography>
												</Grid>
											)}

											<Grid
												item
												xs={12}
												justifyContent="center"
												sx={{
													display: "flex",
													justifyContent: "center",
													textAlign: "center",
												}}>
												<Button
													type="submit"
													disabled={
														!stripe ||
														isLoading ||
														!isCardComplete ||
														Object.keys(errors).length > 0
													}
													variant="contained"
													size="large"
													sx={{
														mt: 3,
														px: 6,
														py: 1.5,
														color: colors.white,
														backgroundColor: buttonColors.infoDarkBlue,
														borderRadius: 2,
														fontWeight: 600,
														textTransform: "none",
														boxShadow: "0 4px 14px 0 rgba(0,118,255,0.39)",
														"&:hover": {
															backgroundColor: buttonColors.infoDarkBlue,
															boxShadow: "0 6px 20px 0 rgba(0,118,255,0.5)",
														},
														"&:disabled": {
															backgroundColor: "rgba(0, 0, 0, 0.12)",
															color: "rgba(0, 0, 0, 0.26)",
															boxShadow: "none",
														},
													}}>
													{isLoading ?
														<LoadingSpinner
															loading={isLoading}
															color={colors.white}
														/>
													: existingCard ?
														"Update Card"
													:	"Add Card"}
												</Button>
											</Grid>
										</Grid>
									</Form>
								)}
							</Formik>
						</Paper>
					</Container>
				</DialogContent>
			</Dialog>

			{openNotificationModal && (
				<NotificationModal
					open={openNotificationModal}
					handleClose={() => setOpenNotificationModal(false)}
					title={
						existingCard ?
							"Card Updated Successfully!"
						:	"Card Added Successfully!"
					}>
					<Grid item>
						<Avatar
							src="/assets/icons/success-animated-icon.gif"
							sx={{ width: 30, height: 30 }}
							alt="Success Icon"
						/>
					</Grid>
					<StyledTypography
						sx={{ fontSize: "1.1rem", color: "text.secondary" }}>
						{existingCard ?
							"Your card has been updated successfully and is ready for use."
						:	"Your card has been registered successfully and is ready for use."}
					</StyledTypography>
				</NotificationModal>
			)}
		</React.Fragment>
	);
};

UpdatePaymentMethodModal.propTypes = {
	open: PropTypes.bool,
	handleClose: PropTypes.func,
};

export default UpdatePaymentMethodModal;
